import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  SceneData,
  CrimeSceneData,
  CrimeSceneSteps,
  MCQuestions,
} from "@/types/crime";

import * as pc from "playcanvas";

type AutoPlayData = {
  time: number;
  delay: number;
};

export interface CrimeSceneState {
  crimeData?: CrimeSceneData;
  setCrimeData: (crimeData: CrimeSceneData) => void;

  progress: number;
  setProgress: (progress: number) => void;

  resetProgressData: number;
  resetProgress: (progress: number) => void;

  simulationData?: SceneData;
  setSimulationData: (data: SceneData) => void;

  focusingCharacter: string;
  setFocusingCharacter: (character: string) => void;

  // focusingCharacterPosition: THREE.Vector3;
  // setFocusingCharacterPosition: (vector3: THREE.Vector3) => void;

  // focusingCharacterAngle: number;
  // setFocusingCharacterAngle: (angle: number) => void;

  cameraPosition: pc.Vec3;
  setCameraPosition: (data: pc.Vec3) => void;

  cameraLookingPosition: pc.Vec3;
  setCameraLookingPosition: (data: pc.Vec3) => void;

  cameraAngle: number;
  setCameraAngle: (data: number) => void;

  simulationCheckpoint: number[];
  setSimulationCheckpoint: (checkpoints: number[]) => void;

  currentLevel: string;
  setCurrentLevel: (level: string) => void;

  currentLevelIndex: number;
  setCurrentLevelIndex: (level: number) => void;

  sceneTime: string;
  setSceneTime: (time: string) => void;

  sceneIndex: number;
  setSceneIndex: (index: number) => void;

  startAutoPlayData: AutoPlayData;
  startAutoPlayDelay: (delay: number) => void;
  resetStartAutoPlayDelay: () => void;

  stopAutoPlayData: number;
  stopAutoPlay: () => void;

  isShownIncidentOccurence: boolean;
  setIsShownIncidentOccurence: (value: boolean) => void;

  crimeSceneSteps: CrimeSceneSteps[];
  setCrimeSceneSteps: (steps: CrimeSceneSteps[]) => void;

  showMCPanel: boolean;
  setShowMCPanel: (value: boolean) => void;

  crimeMCQuestions: MCQuestions[];
  setCrimeMCQuestions: (steps: MCQuestions[]) => void;

  showObjective: boolean;
  setShowObjective: (value: boolean) => void;

  mcScore: number;
  addSceneScore: (value: number) => void;
  resetSceneScore: () => void;

  resetSimulationData: () => void;

  showSceneDialog: boolean;
  setShowSceneDialog: (value: boolean) => void;

  isFinishedAllMc: boolean;
  setIsFinishedAllMc: (value: boolean) => void;

  sceneMessages: string[];
  setSceneMessages: (value: string[]) => void;
  startPlayMessagesData: number;
  startPlayMessages: () => void;
  resetStartPlayMessagesData: () => void;

  hasFinishSceneMessage: boolean;
  setHasFinishSceneMessage: (value: boolean) => void;


}

export const useCrimeSceneStore = create<CrimeSceneState>()(
  persist(
    (set, get) => ({
      setCrimeData: (data) => {
        set({
          crimeData: data,
        });
      },

      progress: 0,
      setProgress: (progress: number) => {
        set({ progress: progress });
      },

      resetProgressData: -1,
      resetProgress: (progress: number) => {
        set({ resetProgressData: progress });
      },

      setSimulationData: (data) => {
        set({
          simulationData: data,
        });
      },

      focusingCharacter: "observationCamera",
      setFocusingCharacter: (character: string) => {
        set({ focusingCharacter: character });
      },

      // focusingCharacterPosition: new THREE.Vector3(0, 0, 0),
      // setFocusingCharacterPosition: (vector3: THREE.Vector3) => {
      //   set({ focusingCharacterPosition: vector3 });
      // },

      // focusingCharacterAngle: 0,
      // setFocusingCharacterAngle: (angle: number) => {
      //   set({ focusingCharacterAngle: angle });
      // },

      cameraPosition: pc.Vec3.ZERO,
      setCameraPosition: (data: pc.Vec3) => {
        set({cameraPosition: data})
      },

      cameraLookingPosition: pc.Vec3.ZERO,
      setCameraLookingPosition: (data: pc.Vec3) => {
        set({cameraLookingPosition: data})
      },

      cameraAngle: 0,
      setCameraAngle: (data: number) => {
        set({cameraAngle: data})
      },

      simulationCheckpoint: [100],
      setSimulationCheckpoint: (checkpoints: number[]) => {
        set({ simulationCheckpoint: checkpoints });
      },

      currentLevel: "",
      setCurrentLevel: (level: string) => {
        set({ currentLevel: level });
      },

      currentLevelIndex: 0,
      setCurrentLevelIndex: (level: number) => {
        set({ currentLevelIndex: level });
      },

      sceneTime: "",
      setSceneTime: (time: string) => {
        set({ sceneTime: time });
      },

      sceneIndex: 0,
      setSceneIndex: (index: number) => {
        set({ sceneIndex: index, showSceneDialog: true });
      },

      startAutoPlayData: { time: -1, delay: -1 },
      startAutoPlayDelay: (delay: number) => {
        set({ startAutoPlayData: { time: Date.now(), delay: delay } });
      },
      resetStartAutoPlayDelay: () => {
        set({ startAutoPlayData: { time: -1, delay: -1 } });
      },

      stopAutoPlayData: -1,
      stopAutoPlay: () => {
        set({ stopAutoPlayData: Date.now() });
      },

      isShownIncidentOccurence: false,
      setIsShownIncidentOccurence: (value: boolean) => {
        set({ isShownIncidentOccurence: value });
      },

      crimeSceneSteps: [],
      setCrimeSceneSteps(steps: CrimeSceneSteps[]) {
        set({ crimeSceneSteps: steps });
      },

      showMCPanel: false,
      setShowMCPanel(value: boolean) {
        set({ showMCPanel: value });
      },

      crimeMCQuestions: [],
      setCrimeMCQuestions(options: MCQuestions[]) {
        set({ crimeMCQuestions: options });
      },

      showObjective: false,
      setShowObjective(value: boolean) {
        set({ showObjective: value });
      },

      resetSimulationData() {
        set({
          showObjective: false,
          crimeMCQuestions: [],
          showMCPanel: false,
          mcScore: 0,
          isFinishedAllMc: false,
          showSceneDialog: true,
        });
      },

      mcScore: 0,
      addSceneScore(value: number) {
        set((state) => ({ mcScore: state.mcScore + value }));
      },
      resetSceneScore() {
        set({ mcScore: 0 });
      },

      showSceneDialog: true,
      setShowSceneDialog(value: boolean) {
        set({ showSceneDialog: value });
      },

      isFinishedAllMc: false,
      setIsFinishedAllMc: (value: boolean) => {
        set({ isFinishedAllMc: value });
      },

      sceneMessages: [],
      setSceneMessages: (value: string[]) => {
        set({ sceneMessages: value });
      },

      startPlayMessagesData: -1,
      startPlayMessages: () => {
        set({ startPlayMessagesData: Date.now() });
      },
      resetStartPlayMessagesData: () => {
        set({ startPlayMessagesData: -1 });
      },

      hasFinishSceneMessage: false,
      setHasFinishSceneMessage: (value: boolean) => {
        set({ hasFinishSceneMessage: value });
      },
    }),
    {
      name: "crime-storage", // localStorage key
      partialize: (state) => ({}),
    }
  )
);
